import { supabase } from "../supabase";
import { formatSupabaseError } from "./estimates";
import { computeLineLabourCost, computeTotalLabourCost, normalizeLogHours } from "./labourCost";
import { findMatchingQuoteLine } from "./projectQuoteLineMatching";
import { LINE_STATUS } from "./invoiceReview";
import { sumMaterialCost, loadMaterialUsageForProject } from "./projectMaterialLedger";
import { sumOtherCostAmounts, loadProjectOtherCosts } from "./projectOtherCosts";
import { loadLabourForProject, loadLabourForProjects } from "./projectLabourLedger";
import { applyOrganizationScopeForActor } from "./orgQueryScope";
import {
  loadProjectQuoteLines,
  loadProjectQuoteSummary,
  QUOTE_LINE_TYPE,
} from "./projectQuoteSnapshots";
import {
  requireFinancialMetricsView,
  requireProjectFinancialView,
} from "./financialGuards";

export const VARIANCE_CATEGORY = Object.freeze({
  MATCHED: "matched",
  QUOTE_ONLY: "quote_only",
  ACTUAL_ONLY: "actual_only",
});

const BILLED_INVOICE_STATUSES = Object.freeze(["sent", "paid"]);

function wrapSupabaseError(error) {
  const wrapped = new Error(formatSupabaseError(error));
  wrapped.cause = error;
  throw wrapped;
}

export function roundCurrency(amount) {
  return Math.round((Number(amount) + Number.EPSILON) * 100) / 100;
}

export function computeQuotedTotal(quoteSummary, quoteLines = []) {
  if (quoteSummary?.quoted_grand_total_snapshot != null) {
    return roundCurrency(Number(quoteSummary.quoted_grand_total_snapshot));
  }

  return roundCurrency(
    (quoteLines || []).reduce(
      (sum, line) => sum + Number(line.quoted_line_total_snapshot || 0),
      0,
    ),
  );
}

export function computeActualCostFromLedgers(
  materialRows = [],
  labourLogs = [],
  otherCostRows = [],
) {
  const materialCost = roundCurrency(sumMaterialCost(materialRows));
  const labourCost = roundCurrency(computeTotalLabourCost(labourLogs));
  const otherCost = roundCurrency(sumOtherCostAmounts(otherCostRows));

  return {
    materialCost,
    labourCost,
    otherCost,
    actualCost: roundCurrency(materialCost + labourCost + otherCost),
  };
}

export function computeBilledTotal(billedLineItems = []) {
  return roundCurrency(
    (billedLineItems || [])
      .filter(
        (line) =>
          (line.line_status || LINE_STATUS.ACTIVE) === LINE_STATUS.ACTIVE,
      )
      .reduce((sum, line) => sum + Number(line.line_total || 0), 0),
  );
}

export function buildProjectFinancialSummary({
  quoteSummary = null,
  quoteLines = [],
  materialRows = [],
  labourLogs = [],
  otherCostRows = [],
  billedLineItems = [],
}) {
  const quoted = computeQuotedTotal(quoteSummary, quoteLines);
  const { materialCost, labourCost, otherCost, actualCost } =
    computeActualCostFromLedgers(materialRows, labourLogs, otherCostRows);
  const billed = computeBilledTotal(billedLineItems);
  const margin = roundCurrency(billed - actualCost);

  return {
    quoted,
    actualCost,
    materialCost,
    labourCost,
    otherCost,
    billed,
    margin,
    hasQuoteSnapshot: Boolean(quoteSummary),
    quotedVsActual: roundCurrency(quoted - actualCost),
    billedVsActual: roundCurrency(billed - actualCost),
  };
}

function sumUsageQuantity(rows = []) {
  return roundCurrency(
    rows.reduce((sum, row) => sum + Number(row.quantity || 0), 0),
  );
}

function sumLabourHours(logs = []) {
  return roundCurrency(logs.reduce((sum, log) => sum + normalizeLogHours(log), 0));
}

export function buildMaterialVarianceRows(quoteLines = [], usageRows = []) {
  const materialQuoteLines = (quoteLines || []).filter(
    (line) => line.line_type === QUOTE_LINE_TYPE.MATERIAL,
  );
  const usageBySnapshotId = new Map();
  const extraUsage = [];

  for (const usage of usageRows || []) {
    let snapshotId = usage.quote_line_snapshot_id || null;

    if (!snapshotId && usage.material_id) {
      const match = findMatchingQuoteLine(materialQuoteLines, {
        lineType: QUOTE_LINE_TYPE.MATERIAL,
        materialId: usage.material_id,
      });
      snapshotId = match?.line_snapshot_id ?? null;
    }

    if (snapshotId) {
      if (!usageBySnapshotId.has(snapshotId)) {
        usageBySnapshotId.set(snapshotId, []);
      }
      usageBySnapshotId.get(snapshotId).push(usage);
    } else {
      extraUsage.push(usage);
    }
  }

  const matchedRows = materialQuoteLines.map((quoteLine) => {
    const linked = usageBySnapshotId.get(quoteLine.line_snapshot_id) || [];
    const actualQuantity = sumUsageQuantity(linked);
    const quotedQuantity = Number(quoteLine.quoted_quantity_snapshot || 0);
    const actualCost = roundCurrency(sumMaterialCost(linked));

    return {
      category:
        linked.length > 0 ? VARIANCE_CATEGORY.MATCHED : VARIANCE_CATEGORY.QUOTE_ONLY,
      lineType: QUOTE_LINE_TYPE.MATERIAL,
      lineSnapshotId: quoteLine.line_snapshot_id,
      description: quoteLine.description_snapshot || "Material",
      quotedQuantity,
      actualQuantity,
      quantityVariance: roundCurrency(actualQuantity - quotedQuantity),
      unitLabel: quoteLine.unit_label_snapshot || "ea",
      quotedLineTotal: Number(quoteLine.quoted_line_total_snapshot || 0),
      actualCost,
      usageCount: linked.length,
    };
  });

  const actualOnlyRows = extraUsage.map((usage) => ({
    category: VARIANCE_CATEGORY.ACTUAL_ONLY,
    lineType: QUOTE_LINE_TYPE.MATERIAL,
    lineSnapshotId: null,
    description: usage.material_name_snapshot || "Material",
    quotedQuantity: 0,
    actualQuantity: Number(usage.quantity || 0),
    quantityVariance: Number(usage.quantity || 0),
    unitLabel: usage.unit_of_measure_snapshot || "ea",
    quotedLineTotal: 0,
    actualCost: Number(usage.total_cost || 0),
    usageCount: 1,
  }));

  return [...matchedRows, ...actualOnlyRows];
}

export function buildLabourVarianceRows(quoteLines = [], labourLogs = []) {
  const labourQuoteLines = (quoteLines || []).filter(
    (line) => line.line_type === QUOTE_LINE_TYPE.LABOUR,
  );
  const logsBySnapshotId = new Map();
  const unmatchedLogs = [];

  for (const log of labourLogs || []) {
    const snapshotId = log.quote_line_snapshot_id || null;

    if (snapshotId) {
      if (!logsBySnapshotId.has(snapshotId)) {
        logsBySnapshotId.set(snapshotId, []);
      }
      logsBySnapshotId.get(snapshotId).push(log);
    } else {
      unmatchedLogs.push(log);
    }
  }

  const matchedRows = labourQuoteLines.map((quoteLine) => {
    const linked = logsBySnapshotId.get(quoteLine.line_snapshot_id) || [];
    const actualHours = sumLabourHours(linked);
    const quotedQuantity = Number(quoteLine.quoted_quantity_snapshot || 0);
    const actualCost = roundCurrency(computeTotalLabourCost(linked));
    const unitLabel = quoteLine.unit_label_snapshot || "hrs";

    return {
      category:
        linked.length > 0 ? VARIANCE_CATEGORY.MATCHED : VARIANCE_CATEGORY.QUOTE_ONLY,
      lineType: QUOTE_LINE_TYPE.LABOUR,
      lineSnapshotId: quoteLine.line_snapshot_id,
      description: quoteLine.description_snapshot || "Labour",
      quotedQuantity,
      actualQuantity: unitLabel === "hrs" ? actualHours : actualHours,
      quantityVariance:
        unitLabel === "hrs"
          ? roundCurrency(actualHours - quotedQuantity)
          : null,
      unitLabel,
      quotedLineTotal: Number(quoteLine.quoted_line_total_snapshot || 0),
      actualCost,
      logCount: linked.length,
    };
  });

  const actualOnlyRows = unmatchedLogs.map((log) => ({
    category: VARIANCE_CATEGORY.ACTUAL_ONLY,
    lineType: QUOTE_LINE_TYPE.LABOUR,
    lineSnapshotId: null,
    description: log.employee || "Labour",
    quotedQuantity: 0,
    actualQuantity: normalizeLogHours(log),
    quantityVariance: normalizeLogHours(log),
    unitLabel: "hrs",
    quotedLineTotal: 0,
    actualCost: roundCurrency(computeLineLabourCost(log)),
    logCount: 1,
  }));

  return [...matchedRows, ...actualOnlyRows];
}

export function buildProjectVarianceReport({
  quoteLines = [],
  materialRows = [],
  labourLogs = [],
}) {
  return {
    material: buildMaterialVarianceRows(quoteLines, materialRows),
    labour: buildLabourVarianceRows(quoteLines, labourLogs),
  };
}

function isDateInRange(value, startDate, endDate) {
  if (!value) return false;
  const date = String(value).slice(0, 10);
  if (startDate && date < startDate) return false;
  if (endDate && date > endDate) return false;
  return true;
}

export async function loadBilledLineItemsForProject(
  projectId,
  { startDate = null, endDate = null, actor = null } = {},
) {
  if (!projectId) return [];

  let invoiceQuery = supabase
    .from("invoices")
    .select("invoice_id, status, sent_at")
    .eq("project_id", projectId)
    .in("status", BILLED_INVOICE_STATUSES);

  if (actor) {
    invoiceQuery = await applyOrganizationScopeForActor(
      invoiceQuery,
      actor,
      supabase,
      { tableName: "invoices" },
    );
  }

  const { data: invoices, error: invoiceError } = await invoiceQuery;

  if (invoiceError) wrapSupabaseError(invoiceError);

  const eligibleInvoices = (invoices || []).filter((invoice) => {
    if (!startDate && !endDate) return true;
    return isDateInRange(invoice.sent_at, startDate, endDate);
  });

  const invoiceIds = eligibleInvoices.map((invoice) => invoice.invoice_id);
  if (!invoiceIds.length) return [];

  const { data: lineItems, error: lineError } = await supabase
    .from("invoice_line_items")
    .select("line_id, line_type, line_total, line_status, invoice_id")
    .in("invoice_id", invoiceIds);

  if (lineError) wrapSupabaseError(lineError);
  return lineItems || [];
}

export async function loadBilledLineItemsForProjects(
  projectIds = [],
  { startDate = null, endDate = null, actor = null } = {},
) {
  if (!projectIds.length) return new Map();

  let invoiceQuery = supabase
    .from("invoices")
    .select("invoice_id, project_id, status, sent_at")
    .in("project_id", projectIds)
    .in("status", BILLED_INVOICE_STATUSES);

  if (actor) {
    invoiceQuery = await applyOrganizationScopeForActor(
      invoiceQuery,
      actor,
      supabase,
      { tableName: "invoices" },
    );
  }

  const { data: invoices, error: invoiceError } = await invoiceQuery;

  if (invoiceError) wrapSupabaseError(invoiceError);

  const eligibleInvoices = (invoices || []).filter((invoice) => {
    if (!startDate && !endDate) return true;
    return isDateInRange(invoice.sent_at, startDate, endDate);
  });

  const invoiceIds = eligibleInvoices.map((invoice) => invoice.invoice_id);
  const linesByProject = new Map(projectIds.map((id) => [id, []]));

  if (!invoiceIds.length) {
    return linesByProject;
  }

  const invoiceProjectById = new Map(
    eligibleInvoices.map((invoice) => [invoice.invoice_id, invoice.project_id]),
  );

  const { data: lineItems, error: lineError } = await supabase
    .from("invoice_line_items")
    .select("line_id, line_type, line_total, line_status, invoice_id")
    .in("invoice_id", invoiceIds);

  if (lineError) wrapSupabaseError(lineError);

  for (const line of lineItems || []) {
    const projectId = invoiceProjectById.get(line.invoice_id);
    if (!projectId) continue;
    linesByProject.get(projectId).push(line);
  }

  return linesByProject;
}

export async function loadQuoteDataForProjects(projectIds = []) {
  if (!projectIds.length) {
    return { summariesByProject: new Map(), linesByProject: new Map() };
  }

  const { data: summaries, error: summaryError } = await supabase
    .from("project_quote_summary_snapshots")
    .select("*")
    .in("project_id", projectIds);

  if (summaryError) wrapSupabaseError(summaryError);

  const { data: lines, error: linesError } = await supabase
    .from("project_quote_line_snapshots")
    .select("*")
    .in("project_id", projectIds)
    .order("sort_order");

  if (linesError) wrapSupabaseError(linesError);

  const summariesByProject = new Map(
    (summaries || []).map((row) => [row.project_id, row]),
  );
  const linesByProject = new Map(projectIds.map((id) => [id, []]));

  for (const line of lines || []) {
    linesByProject.get(line.project_id)?.push(line);
  }

  return { summariesByProject, linesByProject };
}

export async function loadProjectFinancialSummary(actor, projectId) {
  requireProjectFinancialView(actor);

  if (!projectId) {
    throw new Error("projectId is required.");
  }

  const [quoteSummary, quoteLines, materialRows, labourLogs, otherCostRows, billedLineItems] =
    await Promise.all([
      loadProjectQuoteSummary(projectId),
      loadProjectQuoteLines(projectId),
      loadMaterialUsageForProject(projectId, { actor }),
      loadLabourForProject(projectId, {
        select:
          "id, employee, date_iso, total_hours, net_work_seconds, hourly_rate, quote_line_snapshot_id",
        actor,
      }),
      loadProjectOtherCosts(actor, projectId),
      loadBilledLineItemsForProject(projectId, { actor }),
    ]);

  const summary = buildProjectFinancialSummary({
    quoteSummary,
    quoteLines,
    materialRows,
    labourLogs,
    otherCostRows,
    billedLineItems,
  });

  const variance = buildProjectVarianceReport({
    quoteLines,
    materialRows,
    labourLogs,
  });

  return {
    ...summary,
    quoteLines,
    variance,
  };
}

export function enrichAnalyticsRowWithFinancials(
  row,
  {
    quoteSummary = null,
    quoteLines = [],
    billedLineItems = [],
  } = {},
) {
  const quoted = computeQuotedTotal(quoteSummary, quoteLines);
  const billed = computeBilledTotal(billedLineItems);
  const actualCost = Number(row.totalCost || 0);
  const margin = roundCurrency(billed - actualCost);

  return {
    ...row,
    quoted,
    billed,
    margin,
    hasQuoteSnapshot: Boolean(quoteSummary),
  };
}

export function buildProjectHubCostSummaries(
  materialRows = [],
  labourLogs = [],
  projectIds = [],
  otherCostRows = [],
) {
  const materialByProject = new Map(projectIds.map((id) => [id, []]));
  const labourByProject = new Map(projectIds.map((id) => [id, []]));
  const otherByProject = new Map(projectIds.map((id) => [id, []]));

  for (const row of materialRows) {
    if (materialByProject.has(row.project_id)) {
      materialByProject.get(row.project_id).push(row);
    }
  }

  for (const log of labourLogs) {
    if (labourByProject.has(log.project_id)) {
      labourByProject.get(log.project_id).push(log);
    }
  }

  for (const row of otherCostRows) {
    if (otherByProject.has(row.project_id)) {
      otherByProject.get(row.project_id).push(row);
    }
  }

  const summaries = new Map();

  for (const projectId of projectIds) {
    const { materialCost, labourCost, otherCost } = computeActualCostFromLedgers(
      materialByProject.get(projectId) || [],
      labourByProject.get(projectId) || [],
      otherByProject.get(projectId) || [],
    );

    summaries.set(projectId, { materialCost, labourCost, otherCost });
  }

  return summaries;
}

export async function loadProjectHubCostSummaries(actor, projectIds = []) {
  requireProjectFinancialView(actor);

  if (!projectIds.length) {
    return new Map();
  }

  let materialQuery = supabase
    .from("project_material_usage")
    .select("project_id, total_cost")
    .in("project_id", projectIds)
    .is("voided_at", null);

  materialQuery = await applyOrganizationScopeForActor(
    materialQuery,
    actor,
    supabase,
    { tableName: "project_material_usage" },
  );

  let otherQuery = supabase
    .from("project_other_costs")
    .select("project_id, amount, is_active")
    .in("project_id", projectIds)
    .eq("is_active", true);

  otherQuery = await applyOrganizationScopeForActor(
    otherQuery,
    actor,
    supabase,
    { tableName: "project_other_costs" },
  );

  const [materialResult, labourLogs, otherResult] = await Promise.all([
    materialQuery,
    loadLabourForProjects(projectIds, { actor }),
    otherQuery,
  ]);

  if (materialResult.error) {
    wrapSupabaseError(materialResult.error);
  }

  if (otherResult.error) {
    wrapSupabaseError(otherResult.error);
  }

  return buildProjectHubCostSummaries(
    materialResult.data || [],
    labourLogs,
    projectIds,
    otherResult.data || [],
  );
}

export async function loadFinancialMetricsForAnalytics(
  actor,
  projectIds = [],
  { startDate = null, endDate = null } = {},
) {
  requireFinancialMetricsView(actor);

  if (!projectIds.length) {
    return {
      summariesByProject: new Map(),
      linesByProject: new Map(),
      billedLinesByProject: new Map(),
    };
  }

  const [{ summariesByProject, linesByProject }, billedLinesByProject] =
    await Promise.all([
      loadQuoteDataForProjects(projectIds),
      loadBilledLineItemsForProjects(projectIds, { startDate, endDate, actor }),
    ]);

  return {
    summariesByProject,
    linesByProject,
    billedLinesByProject,
  };
}
